import { format } from 'date-fns';
import type { TicketAuditEntry } from '@/services/tech-support.service';

const ACTION_LABELS: Record<string, (e: TicketAuditEntry) => string> = {
  created:          (_e) => `Ticket created`,
  status_changed:   (e) => `Status changed to "${(e.new_values as { status?: string })?.status ?? ''}"`,
  assigned:         (e) => {
    const name = (e.new_values as { assigned_to_name?: string })?.assigned_to_name;
    return name ? `Assigned to ${name}` : 'Unassigned';
  },
  unassigned:       () => 'Removed assignee',
  priority_changed: (e) => `Priority changed to "${(e.new_values as { priority?: string })?.priority ?? ''}"`,
  escalated:        (e) => `Escalated to level ${(e.new_values as { level?: number })?.level ?? ''}`,
  comment_added:    () => 'Comment added',
  comment_deleted:  () => 'Comment deleted',
  updated:          () => 'Ticket details updated',
};

interface Props {
  auditLog: TicketAuditEntry[];
}

export function TicketActivityTimeline({ auditLog }: Props) {
  if (auditLog.length === 0) {
    return <p className="text-sm text-gray-400 italic">No activity yet.</p>;
  }

  return (
    <div className="space-y-0">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Activity</h3>
      <div className="relative">
        <div className="absolute left-3.5 top-0 bottom-0 w-px bg-teal-100" />
        <div className="space-y-4">
          {auditLog.map((entry) => {
            const labelFn = ACTION_LABELS[entry.action] ?? (() => entry.action);
            return (
              <div key={entry.id} className="relative flex gap-3 pl-8">
                <div className="absolute left-2.5 top-1.5 w-2 h-2 rounded-full bg-teal-400 border-2 border-white" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-700">{labelFn(entry)}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {entry.actor.name} · {format(new Date(entry.created_at), 'MMM d, h:mm a')}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
